import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { useContent } from "../../hooks/useContent";
import { useAuth } from "../../hooks/useAuth";
import { get_home_dashboard_api } from "../../DAL/HomeDashboard";
import moment from "moment";

const Home = () => {
  const navigate = useNavigate();
  const { setPageHeading } = useContent();
  const { userInfo } = useAuth();
  const [stats, setStats] = useState({
    totalOrganizations: 0,
    activeOrganizations: 0,
    totalTickets: 0,
    openTickets: 0,
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  const quickActions = [
    {
      title: "Create Organization",
      icon: "mdi:office-building",
      link: "/create-organization",
      color: "#137fec",
      gradient: "linear-gradient(135deg, #3b82f6 0%, #1a73e8 100%)",
    },
    {
      title: "Manage Organizations",
      icon: "mdi:account-edit",
      link: "/manage-organizations",
      color: "#137fec",
      gradient: "linear-gradient(135deg, #1a73e8 0%, #3b82f6 100%)",
    },
    {
      title: "Support Tickets",
      description: "Create and manage tickets",
      icon: "mdi:ticket-confirmation",
      link: "/support-tickets",
      color: "#137fec",
      gradient: "linear-gradient(135deg, #3b82f6 0%, #1a73e8 100%)",
    },
    {
      title: "Email Support",
      description: "Send support request via email",
      icon: "mdi:email-fast",
      link: "/email-support",
      color: "#137fec",
      gradient: "linear-gradient(135deg, #1a73e8 0%, #3b82f6 100%)",
    },
    // {
    //   title: "Web Conference",
    //   description: "Schedule or join meetings",
    //   icon: "mdi:video-box",
    //   link: "/meetings",
    //   color: "#137fec",
    //   gradient: "linear-gradient(135deg, #3b82f6 0%, #1a73e8 100%)",
    // },
    {
      title: "Partner Reference",
      description: "Access documentation",
      icon: "mdi:book-open-page-variant",
      link: "http://partnerreference.myveekrypt.com",
      color: "#137fec",
      gradient: "linear-gradient(135deg, #1a73e8 0%, #3b82f6 100%)",
    },
  ];

  const statCards = [
    {
      title: "Total Organizations",
      value: stats.totalOrganizations,
      icon: "mdi:office-building",
      color: "#137fec",
      bgColor: "hsl(210 100% 51% / 0.15)",
      link: "/manage-organizations",
    },
    {
      title: "Support Tickets",
      value: stats.totalTickets,
      icon: "mdi:ticket",
      color: "#137fec",
      bgColor: "hsl(210 100% 51% / 0.15)",
      link: "/support-tickets",
    },
    {
      title: "Open Tickets",
      value: stats.openTickets,
      icon: "mdi:alert-circle",
      color: "#ef4444",
      bgColor: "hsl(0 84% 60% / 0.15)",
      link: "/support-tickets",
    },
  ];

  useEffect(() => {
    setPageHeading("Partner Portal Dashboard");
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStats = async () => {
    setIsLoadingStats(true);
    try {
      // Load dashboard statistics (counts only)
      const dashboardResult = await get_home_dashboard_api({});
      
      if (dashboardResult.code === 200 && dashboardResult.stats) {
        setStats({
          totalOrganizations: dashboardResult.stats.totalOrganizations || dashboardResult.stats.totalCompanies || 0,
          activeOrganizations: dashboardResult.stats.activeOrganizations || dashboardResult.stats.activeCompanies || 0,
          totalTickets: dashboardResult.stats.totalTickets || 0,
          openTickets: dashboardResult.stats.openTickets || 0,
        });
      } else {
        // If API call fails, set defaults
        setStats({
          totalOrganizations: 0,
          activeOrganizations: 0,
          totalTickets: 0,
          openTickets: 0,
        });
      }
    } catch (error) {
      setStats({
        totalOrganizations: 0,
        activeOrganizations: 0,
        totalTickets: 0,
        openTickets: 0,
      });
    }
    setIsLoadingStats(false);
  };

  const handleClick = (link) => {
    if (link.startsWith("http")) {
      window.open(link, "_blank", "noopener noreferrer");
    } else {
      navigate(link);
    }
  };

  const getGreeting = () => {
    const hour = moment().hour();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  return (
    <div className="Home">
      <div className="container">
        {/* Welcome Section */}
        <div className="welcome-section mb-4">
          <div className="welcome-card">
            <div className="d-flex align-items-center justify-content-between flex-wrap">
              <div>
                <h3 className="mb-2">
                  {getGreeting()},{" "}
                  <span className="fw-bold">
                    {userInfo?.first_name || "Partner"}
                  </span>
                  !
                </h3>
                <p className="text-muted mb-0">
                  Welcome to your Partner Portal Dashboard. Manage your organization
                  accounts and support tickets efficiently.
                </p>
              </div>
              <div className="welcome-icon">
                <Icon icon="mdi:hand-wave" width="48" />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-section mb-4">
          <div className="row g-3">
            {statCards.map((stat, index) => (
              <div key={index} className="col-12 col-sm-6 col-lg-4">
                <div
                  className="stat-card"
                  onClick={() => handleClick(stat.link)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="stat-icon" style={{ color: stat.color }}>
                    <div
                      className="icon-wrapper"
                      style={{ backgroundColor: stat.bgColor }}
                    >
                      <Icon icon={stat.icon} width="32" />
                    </div>
                  </div>
                  <div className="stat-content">
                    <div className="stat-value" style={{ color: stat.color }}>
                      {isLoadingStats ? (
                        <Icon icon="mdi:loading" className="spin-animation" />
                      ) : (
                        stat.value
                      )}
                    </div>
                    <div className="stat-title">{stat.title}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions Section */}
        <div className="quick-actions-section mb-4">
          <div className="section-header mb-3">
            <h5 className="mb-0 d-flex align-items-center gap-2">
              <Icon icon="mdi:lightning-bolt" width="24" />
              Quick Actions
            </h5>
          </div>
          <div className="row g-3">
            {quickActions.map((action, index) => (
              <div key={index} className="col-12 col-md-6 col-lg-4">
                <div
                  className="quick-action-card"
                  onClick={() => handleClick(action.link)}
                >
                  <div
                    className="action-icon-wrapper"
                    style={{ background: action.gradient }}
                  >
                    <Icon icon={action.icon} width="28" color="white" />
                  </div>
                  <div className="action-content">
                    <h6 className="action-title">{action.title}</h6>
                    <p className="action-description">{action.description}</p>
                  </div>
                  <div className="action-arrow">
                    <Icon
                      icon="mdi:arrow-right"
                      width="20"
                      style={{ color: action.color }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Help Section */}
        <div className="help-section">
          <div className="help-card">
            <div className="d-flex align-items-center gap-3">
              <div
                className="help-icon"
                style={{
                  background:
                    "linear-gradient(135deg, #3b82f6 0%, #1a73e8 100%)",
                }}
              >
                <Icon icon="mdi:help-circle" width="32" color="white" />
              </div>
              <div className="flex-fill">
                <h6 className="mb-1">Need Help?</h6>
                <p className="text-muted mb-0 small">
                  Contact our support team or check the partner reference guide
                  for assistance.
                </p>
              </div>
              <button
                className="theme-button"
                onClick={() => handleClick("/email-support")}
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
